import { useRouter } from 'next/router';
import { lazy, Suspense, useContext, useEffect, useRef } from 'react';

import { Typography } from '@mui/material';
import {
  MODAL_STATUS_CODE,
  ModalDispatchContext,
  ModalStateContext,
  useServiceAppState,
} from '@kc-console/context';
import { useTranslation } from '@kc-console/core';

import type { ModalStateParams, OnCloseModalParams } from '@kc-console/context';
import type { ModalClosingConfirmProps } from '../ModalClosingConfirm';

const ModalRenderer = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const openedModals = useContext(ModalStateContext);
  const openedModalsRef = useRef(openedModals);
  openedModalsRef.current = openedModals;
  const { open, close, closeAll, exited } = useContext(ModalDispatchContext);
  const {
    state: { status },
  } = useServiceAppState();
  const statusRef = useRef(status);
  statusRef.current = status;

  useEffect(() => {
    if (MODAL_STATUS_CODE.includes(statusRef.current)) return;
    if (openedModalsRef.current.length > 0) {
      closeAll();
    }
  }, [router, closeAll]);

  return (
    <Suspense fallback={<></>}>
      {openedModals.map(modal => {
        const { Component, props = {}, uniqueId } = modal;
        const {
          onSubmit,
          autoClose = true,
          modalClosingConfirmProps = {
            title: t('layout:modal.title.notification'),
            description: (
              <Typography variant="body/normal/md">
                {t('layout:modal.temp_save.has_content')}
                <br />
                {t('layout:modal.temp_save.warning_msg')}
              </Typography>
            ),
          },
          ...otherProps
        } = props;

        const onClose = (params?: OnCloseModalParams) => {
          const {
            preventDirectClose = false,
            beforeClose = () => {
              /** */
            },
          } = params || {};
          const { title, description } = modalClosingConfirmProps;

          if (!preventDirectClose || !modalClosingConfirmProps) {
            return close(modal);
          }

          open({
            Component: lazy(() => import('../ModalClosingConfirm')),
            props: {
              onSubmit: () => {
                beforeClose();
                close(modal);
              },
              autoClose: true,
              title,
              description,
            },
          } as ModalStateParams<ModalClosingConfirmProps>);
        };

        const handleSubmit = async () => {
          if (typeof onSubmit === 'function') {
            await onSubmit();
          }

          if (autoClose) {
            onClose();
          }
        };

        const handleExited = () => exited(modal);

        return (
          <Component
            {...otherProps}
            key={uniqueId}
            onClose={onClose}
            onSubmit={handleSubmit}
            onExited={handleExited}
            TransitionProps={{ onExited: handleExited }}
          />
        );
      })}
    </Suspense>
  );
};

export default ModalRenderer;
